import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";

const W_STYLES = {
  REQUESTED: "border-blue-500/30 bg-blue-500/10 text-blue-400",
  APPROVED: "border-purple-500/30 bg-purple-500/10 text-purple-400",
  PAID: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  REJECTED: "border-rose-500/30 bg-rose-500/10 text-rose-400",
};
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Withdrawals() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const isOwner = (user.role === "label" && user.label_id) || (user.role === "artist" && !user.label_id);
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [payOpen, setPayOpen] = useState(null);
  const [headers, setHeaders] = useState([{ k: "Transaction ID", v: "" }, { k: "Mode", v: "" }, { k: "Date", v: "" }]);

  const load = () => {
    api.get("/withdrawals").then((r) => setRows(r.data));
    if (isOwner) api.get("/balance").then((r) => setBalance(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const request = async () => {
    try {
      await api.post("/withdrawals", { amount: Number(amount) });
      toast.success("Withdrawal requested");
      setOpen(false); setAmount(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = async (id, action) => { await api.post(`/withdrawals/${id}/${action}`); toast.success(action); load(); };
  const pay = async () => {
    const details = {};
    headers.forEach((h) => { if (h.k) details[h.k] = h.v; });
    if (payOpen.status === "PAID") {
      await api.put(`/withdrawals/${payOpen.id}`, { payout_details: details });
      toast.success("Payout details updated");
    } else {
      await api.post(`/withdrawals/${payOpen.id}/pay`, { payout_details: details });
      toast.success("Marked paid");
    }
    setPayOpen(null); load();
  };

  return (
    <div className="space-y-6" data-testid="withdrawals-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight">Withdrawals</h1><p className="mt-1 text-muted-foreground">{isAdmin ? "Approve requests and record payouts." : "Request payouts from your available balance."}</p></div>
        {isOwner && <Button onClick={() => setOpen(true)} data-testid="new-withdrawal-btn"><Plus className="mr-2 h-4 w-4" />Request Withdrawal</Button>}
      </div>

      {isOwner && balance && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[["Earned", balance.earned], ["Adjustment", balance.adjustment], ["Paid", balance.paid], ["Available", balance.available]].map(([k, v]) => (
            <Card key={k} className="p-4" data-testid={`balance-${k.toLowerCase()}`}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{k}</p>
              <p className={`mt-1 text-xl font-bold ${k === "Available" ? "text-emerald-400" : ""}`}>{inr(v)}</p>
            </Card>
          ))}
        </div>
      )}

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Owner</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Payout Details</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No withdrawals.</TableCell></TableRow>}
            {rows.map((w) => (
              <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                <TableCell className="font-medium">{w.owner_name}</TableCell>
                <TableCell className="text-right font-mono">{inr(w.amount)}</TableCell>
                <TableCell><Badge variant="outline" className={W_STYLES[w.status]}>{w.status}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{w.payout_details && Object.keys(w.payout_details).length ? Object.entries(w.payout_details).map(([k, v]) => `${k}: ${v}`).join(", ") : "—"}</TableCell>
                <TableCell className="text-right">
                  {isAdmin && w.status === "REQUESTED" && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => act(w.id, "approve")} data-testid={`approve-wd-${w.id}`}>Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => act(w.id, "reject")} data-testid={`reject-wd-${w.id}`}>Reject</Button>
                    </div>
                  )}
                  {isAdmin && w.status === "APPROVED" && <Button size="sm" onClick={() => setPayOpen(w)} data-testid={`pay-wd-${w.id}`}><Wallet className="mr-2 h-4 w-4" />Mark Paid</Button>}
                  {isAdmin && w.status === "PAID" && <Button size="sm" variant="secondary" onClick={() => setPayOpen(w)} data-testid={`edit-wd-${w.id}`}>Edit</Button>}
                  {w.status === "REJECTED" && <span className="text-xs text-muted-foreground">Closed</span>}
                  {!isAdmin && w.status === "REQUESTED" && <span className="text-xs text-muted-foreground">Awaiting approval</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Withdrawal</DialogTitle></DialogHeader>
          {balance && <p className="text-sm text-muted-foreground">Available: <span className="font-semibold text-emerald-400">{inr(balance.available)}</span></p>}
          <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdrawal-amount" className="mt-1" /></div>
          {amount && Number(amount) < 5000 && <p className="text-xs text-amber-500" data-testid="threshold-warning">Request is below threshold value.</p>}
          <DialogFooter><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={request} data-testid="submit-withdrawal-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payout — {payOpen && inr(payOpen.amount)}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Add any transaction detail headers (fully customisable).</p>
          <div className="space-y-2">
            {headers.map((h, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Header" value={h.k} onChange={(e) => setHeaders(headers.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} data-testid={`payout-key-${i}`} />
                <Input placeholder="Value" value={h.v} onChange={(e) => setHeaders(headers.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} data-testid={`payout-val-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => setHeaders(headers.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
              </div>
            ))}
            <Button variant="secondary" size="sm" onClick={() => setHeaders([...headers, { k: "", v: "" }])} data-testid="add-payout-header"><Plus className="mr-1 h-4 w-4" />Add Header</Button>
          </div>
          <DialogFooter><Button variant="secondary" onClick={() => setPayOpen(null)}>Cancel</Button><Button onClick={pay} data-testid="confirm-pay-btn">Confirm Paid</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
